import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, ClipboardList, Download, Edit3, FileSpreadsheet, FileText, Loader2, Mic2, Plus, Search, Settings, Star, Trash2, Upload, Users, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import jsPDF from "jspdf";
import Papa from "papaparse";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIAS = {
  "masculino_grave": "Masculino - Grave",
  "masculino_aguda": "Masculino - Aguda",
  "feminino_grave": "Feminino - Grave",
  "feminino_aguda": "Feminino - Aguda"
};
const calcNota = (d) => ((d.disponibilidade + d.qualidade + d.som + d.comprometimento) / 4);
const getNotaColor = (n) => n <= 2 ? "text-red-400" : n <= 3 ? "text-amber-400" : "text-emerald-400";
const getCatColor = (c) => !c ? "bg-slate-700 text-slate-400" : c.includes("masculino") ? "bg-blue-500/20 text-blue-400" : "bg-pink-500/20 text-pink-400";

const StarRating = ({ value, onChange, readonly = false, size = "md" }) => {
  const [hovered, setHovered] = useState(0);
  const s = { sm: "w-4 h-4", md: "w-5 h-5" };
  return (
    <div className="flex gap-0.5 items-center" onMouseLeave={() => !readonly && setHovered(0)}>
      {[1, 2, 3, 4, 5].map(star => (
        <button key={star} type="button" disabled={readonly} onClick={() => !readonly && onChange(star)}
          onMouseEnter={() => !readonly && setHovered(star)} className={readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110 transition-transform'}>
          <Star className={`${s[size]} transition-colors ${star <= (hovered || value) ? "text-yellow-400 fill-yellow-400" : "text-slate-600"}`} />
        </button>
      ))}
      <span className="ml-1.5 text-xs text-slate-400 font-mono">{value}/5</span>
    </div>
  );
};

export const OrganizacaoDubladores = ({ getAuthHeaders }) => {
  const [dubladores, setDubladores] = useState([]);
  const [teams, setTeams] = useState([]);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("");
  const [sortBy, setSortBy] = useState("nome");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
  const [editingDublador, setEditingDublador] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteTeamConfirm, setDeleteTeamConfirm] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState("adicionar");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);
  const [customCategories, setCustomCategories] = useState([]);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [siteUsers, setSiteUsers] = useState([]);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showFeedbackHistory, setShowFeedbackHistory] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackForm, setFeedbackForm] = useState({ titulo: '', descricao: '', dublador_id: '', dublador_nome: '' });
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const headers = getAuthHeaders();

  const fetchData = useCallback(async () => {
    try {
      const [dRes, tRes, cRes, uRes] = await Promise.all([
        axios.get(`${API}/tools/ratings/dubladores`, { headers }),
        axios.get(`${API}/tools/ratings/teams`, { headers }),
        axios.get(`${API}/tools/ratings/categorias`, { headers }),
        axios.get(`${API}/community/members`, { headers }).catch(() => ({ data: [] }))
      ]);
      setDubladores(dRes.data);
      setTeams(tRes.data);
      setCustomCategories(cRes.data);
      setSiteUsers(uRes.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = dubladores.filter(d => {
    const matchTeam = !currentTeamId || d.team_id === currentTeamId;
    const matchSearch = !searchTerm || d.nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = !filterCategoria || filterCategoria === "all" || d.categoria === filterCategoria;
    return matchTeam && matchSearch && matchCat;
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "nome") cmp = a.nome.localeCompare(b.nome, 'pt-BR');
    else if (sortBy === "nota_geral") cmp = calcNota(a) - calcNota(b);
    else cmp = (a[sortBy] || 0) - (b[sortBy] || 0);
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const handleSort = (f) => { if (sortBy === f) setSortOrder(o => o === "asc" ? "desc" : "asc"); else { setSortBy(f); setSortOrder(f === "nome" ? "asc" : "desc"); } };

  // CRUD Dublador
  const saveDublador = async (formData) => {
    try {
      if (editingDublador) {
        const res = await axios.put(`${API}/tools/ratings/dubladores/${editingDublador.id}`, formData, { headers });
        setDubladores(dubladores.map(d => d.id === editingDublador.id ? res.data : d));
        toast.success("Atualizado!");
      } else {
        const res = await axios.post(`${API}/tools/ratings/dubladores`, { ...formData, team_id: currentTeamId }, { headers });
        setDubladores([...dubladores, res.data]);
        toast.success("Adicionado!");
      }
      setIsDialogOpen(false); setEditingDublador(null);
    } catch { toast.error("Erro ao salvar"); }
  };

  const deleteDublador = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/tools/ratings/dubladores/${deleteConfirm.id}`, { headers });
      setDubladores(dubladores.filter(d => d.id !== deleteConfirm.id));
      setDeleteConfirm(null);
      toast.success("Removido!");
    } catch { toast.error("Erro ao remover"); }
  };

  // CRUD Team
  const saveTeam = async (nome) => {
    try {
      if (editingTeam) {
        const res = await axios.put(`${API}/tools/ratings/teams/${editingTeam.id}`, { nome }, { headers });
        setTeams(teams.map(t => t.id === editingTeam.id ? res.data : t));
        toast.success("Equipe atualizada!");
      } else {
        const res = await axios.post(`${API}/tools/ratings/teams`, { nome }, { headers });
        setTeams([...teams, res.data]);
        setCurrentTeamId(res.data.id);
        toast.success("Equipe criada!");
      }
      setIsTeamDialogOpen(false); setEditingTeam(null);
    } catch { toast.error("Erro ao salvar equipe"); }
  };

  const deleteTeam = async () => {
    if (!deleteTeamConfirm) return;
    try {
      await axios.delete(`${API}/tools/ratings/teams/${deleteTeamConfirm.id}`, { headers });
      setTeams(teams.filter(t => t.id !== deleteTeamConfirm.id));
      setDubladores(dubladores.filter(d => d.team_id !== deleteTeamConfirm.id));
      if (currentTeamId === deleteTeamConfirm.id) setCurrentTeamId(null);
      setDeleteTeamConfirm(null);
      toast.success("Equipe excluída!");
    } catch { toast.error("Erro ao excluir"); }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await axios.post(`${API}/tools/ratings/categorias`, { nome: newCatName.trim() }, { headers });
      setCustomCategories([...customCategories, res.data]);
      setNewCatName('');
      toast.success("Categoria criada!");
    } catch { toast.error("Erro"); }
  };

  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/tools/ratings/categorias/${id}`, { headers });
      setCustomCategories(customCategories.filter(c => c.id !== id));
      toast.success("Categoria removida!");
    } catch { toast.error("Erro"); }
  };

  // Feedback functions
  const fetchFeedbacks = async () => {
    try {
      const res = await axios.get(`${API}/tools/feedbacks`, { headers });
      setFeedbacks(res.data || []);
    } catch (err) { console.error('Failed to fetch feedbacks:', err); }
  };

  const submitFeedback = async () => {
    if (!feedbackForm.titulo.trim() || !feedbackForm.descricao.trim()) return toast.error("Preencha título e descrição");
    try {
      await axios.post(`${API}/tools/feedbacks`, feedbackForm, { headers: { ...headers, 'Content-Type': 'application/json' } });
      toast.success("Feedback enviado!");
      setShowFeedbackDialog(false);
      setFeedbackForm({ titulo: '', descricao: '', dublador_id: '', dublador_nome: '' });
    } catch { toast.error("Erro ao enviar"); }
  };

  const deleteFeedback = async (id) => {
    try {
      await axios.delete(`${API}/tools/feedbacks/${id}`, { headers });
      setFeedbacks(feedbacks.filter(f => f.id !== id));
      toast.success("Feedback removido!");
    } catch { toast.error("Erro"); }
  };

  // Build all categories (default + custom)
  const allCategories = {
    ...CATEGORIAS,
    ...Object.fromEntries(customCategories.map(c => [c.id, c.nome]))
  };

  const getCatLabel = (d) => {
    // Support multiple categories
    const cats = d.categorias || [];
    if (cats.length > 0) return cats.map(c => allCategories[c] || c).join(', ');
    if (d.categoria) return allCategories[d.categoria] || CATEGORIAS[d.categoria] || d.categoria;
    return '';
  };

  const exportCSV = () => {
    const data = sorted.map(d => ({ Nome: d.nome, Categoria: getCatLabel(d), Disponibilidade: d.disponibilidade, Qualidade: d.qualidade, Som: d.som, Comprometimento: d.comprometimento, "Nota Geral": calcNota(d).toFixed(2), Observacoes: d.observacoes }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const link = document.createElement("a"); link.href = url; link.download = "dubladores.csv";
    document.body.appendChild(link); link.click(); document.body.removeChild(link); toast.success("CSV exportado!");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const team = teams.find(t => t.id === currentTeamId);
    doc.setFontSize(18); doc.text(team ? `Equipe: ${team.nome}` : "Lista de Dubladores", 20, 20);
    doc.setFontSize(10); let y = 35;
    sorted.forEach((d, i) => {
      if (y > 270) { doc.addPage(); y = 20; }
      const ng = calcNota(d);
      doc.setFontSize(12); doc.text(`${i + 1}. ${d.nome}`, 20, y);
      doc.setFontSize(10); doc.text(`Nota: ${ng.toFixed(2)}/5 | Cat: ${getCatLabel(d) || "N/A"}`, 20, y + 6);
      doc.text(`Disp: ${d.disponibilidade} | Qual: ${d.qualidade} | Som: ${d.som} | Comp: ${d.comprometimento}`, 20, y + 12);
      y += d.observacoes ? 26 : 20;
    });
    doc.save("dubladores.pdf"); toast.success("PDF exportado!");
  };

  const exportListaExcel = async () => {
    try {
      const res = await axios.get(`${API}/tools/ratings/dubladores/exportar`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.download = 'organizacao_dubladores.xlsx';
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("Lista exportada em Excel!");
    } catch { toast.error("Erro ao exportar lista"); }
  };

  const handleImportFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) { setImportFile(f); setImportDialogOpen(true); }
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const importarLista = async () => {
    if (!importFile) return toast.error("Selecione um arquivo");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('modo', importMode);
      if (currentTeamId) fd.append('team_id', currentTeamId);
      const res = await axios.post(`${API}/tools/ratings/dubladores/importar`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      setImportDialogOpen(false); setImportFile(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao importar"); }
    finally { setImporting(false); }
  };

  const sortOpts = [
    { value: "nome", label: "Nome" }, { value: "disponibilidade", label: "Disponibilidade" },
    { value: "qualidade", label: "Qualidade" }, { value: "som", label: "Som" },
    { value: "comprometimento", label: "Comprometimento" }, { value: "nota_geral", label: "Nota Geral" }
  ];

  return (
    <div className="space-y-6" data-testid="organizacao-dubladores">
      {/* Dublador Dialog */}
      <DubladorFormDialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) setEditingDublador(null); }}
        editingDublador={editingDublador} onSave={saveDublador} customCategories={customCategories} siteUsers={siteUsers} />

      {/* Team Dialog */}
      <TeamFormDialog open={isTeamDialogOpen} onOpenChange={(o) => { setIsTeamDialogOpen(o); if (!o) setEditingTeam(null); }}
        editingTeam={editingTeam} onSave={saveTeam} />

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-400" /> Confirmar Exclusão</DialogTitle>
            <DialogDescription className="text-slate-400">Excluir <strong className="text-white">{deleteConfirm?.nome}</strong>?</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={deleteDublador} className="bg-red-600 hover:bg-red-700" data-testid="org-confirm-delete">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Confirm */}
      <Dialog open={!!deleteTeamConfirm} onOpenChange={() => setDeleteTeamConfirm(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-400" /> Excluir Equipe</DialogTitle>
            <DialogDescription className="text-slate-400">Excluir <strong className="text-white">{deleteTeamConfirm?.nome}</strong>?<br /><span className="text-red-400 font-medium">Todos os dubladores serão removidos!</span></DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTeamConfirm(null)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={deleteTeam} className="bg-red-600 hover:bg-red-700" data-testid="org-confirm-delete-team">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(o) => { setImportDialogOpen(o); if (!o) setImportFile(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-teal-400" /> Importar Lista de Dubladores</DialogTitle>
            <DialogDescription className="text-slate-400">{importFile ? `Arquivo: ${importFile.name}` : "Selecione um arquivo Excel (.xlsx)"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" value="adicionar" checked={importMode === "adicionar"} onChange={(e) => setImportMode(e.target.value)} className="accent-teal-500" />
                <span className="text-white text-sm">Adicionar à lista</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" value="substituir" checked={importMode === "substituir"} onChange={(e) => setImportMode(e.target.value)} className="accent-teal-500" />
                <span className="text-white text-sm">Substituir lista{currentTeamId ? " da equipe" : " inteira"}</span>
              </label>
            </div>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-3">
              <p className="text-xs text-slate-300 font-medium mb-2">Formato esperado (.xlsx):</p>
              <div className="text-xs text-slate-400 space-y-0.5">
                <p><strong>A:</strong> NOME | <strong>B:</strong> CATEGORIA | <strong>C:</strong> DISPONIBILIDADE (0-5)</p>
                <p><strong>D:</strong> QUALIDADE (0-5) | <strong>E:</strong> SOM (0-5) | <strong>F:</strong> COMPROMETIMENTO (0-5)</p>
                <p><strong>G:</strong> OBSERVAÇÕES | <strong>H:</strong> EQUIPE (nome)</p>
              </div>
              <p className="text-xs text-teal-400 mt-2">Dica: Exporte primeiro para obter o formato correto.</p>
            </div>
            {currentTeamId && (
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3">
                <p className="text-sm text-teal-400">Equipe atual: <strong>{teams.find(t => t.id === currentTeamId)?.nome}</strong></p>
                <p className="text-xs text-slate-400 mt-1">Dubladores sem equipe no Excel serão vinculados a esta equipe.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={importarLista} disabled={!importFile || importing} className="bg-teal-600 hover:bg-teal-700" data-testid="org-confirm-import">
              {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <input type="file" ref={importFileRef} onChange={handleImportFileSelect} accept=".xlsx,.xls" className="hidden" />

      {/* Category Management Dialog */}
      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Settings className="w-5 h-5 text-purple-400" /> Gerenciar Categorias</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Nova categoria..." className="bg-slate-900 border-slate-600 text-white" data-testid="org-new-cat-input"
                onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }} />
              <Button onClick={addCategory} className="bg-purple-600 hover:bg-purple-700 px-3" data-testid="org-add-cat"><Plus className="w-4 h-4" /></Button>
            </div>
            {customCategories.length === 0 && <p className="text-slate-500 text-sm text-center py-4">Nenhuma categoria criada</p>}
            {customCategories.map(c => (
              <div key={c.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
                <span className="text-sm text-white">{c.nome}</span>
                <button onClick={() => deleteCategory(c.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`org-del-cat-${c.id}`}><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatDialog(false)} className="border-slate-600 text-slate-300">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><FileText className="w-5 h-5 text-orange-400" /> Enviar Feedback</DialogTitle>
            <DialogDescription className="text-slate-400">Relate um feedback sobre um dublador (opcional).</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Dublador (opcional)</label>
              <select value={feedbackForm.dublador_id} onChange={(e) => {
                const sel = siteUsers.find(u => u.id === e.target.value);
                setFeedbackForm({...feedbackForm, dublador_id: e.target.value, dublador_nome: sel ? (sel.artist_name || sel.name) : ''});
              }} className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" data-testid="feedback-dublador-select">
                <option value="">-- Nenhum --</option>
                {siteUsers.filter(u => u.role === 'dublador').map(u => (
                  <option key={u.id} value={u.id}>{u.artist_name || u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Título *</label>
              <Input value={feedbackForm.titulo} onChange={(e) => setFeedbackForm({...feedbackForm, titulo: e.target.value})} className="bg-slate-900 border-slate-600 text-white" data-testid="feedback-titulo" />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Descrição *</label>
              <Textarea value={feedbackForm.descricao} onChange={(e) => setFeedbackForm({...feedbackForm, descricao: e.target.value})} className="bg-slate-900 border-slate-600 text-white min-h-[100px]" data-testid="feedback-descricao" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackDialog(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={submitFeedback} className="bg-orange-600 hover:bg-orange-700" data-testid="feedback-submit">Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback History Dialog */}
      <Dialog open={showFeedbackHistory} onOpenChange={setShowFeedbackHistory}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><ClipboardList className="w-5 h-5 text-indigo-400" /> Histórico de Feedbacks</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {feedbacks.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Nenhum feedback registrado</p>
            ) : feedbacks.map(f => (
              <div key={f.id} className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 cursor-pointer hover:border-slate-600 transition-colors" 
                onClick={() => setSelectedFeedback(selectedFeedback?.id === f.id ? null : f)} data-testid={`feedback-item-${f.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium text-sm truncate">{f.titulo}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      {f.dublador_nome && <span className="text-xs text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">{f.dublador_nome}</span>}
                      <span className="text-xs text-slate-500">por {f.author_name}</span>
                      <span className="text-xs text-slate-600">{f.created_at?.split('T')[0]}</span>
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); deleteFeedback(f.id); }} className="text-red-400 hover:text-red-300 p-1 shrink-0" data-testid={`del-feedback-${f.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
                {selectedFeedback?.id === f.id && (
                  <div className="mt-3 pt-3 border-t border-slate-700">
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{f.descricao}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackHistory(false)} className="border-slate-600 text-slate-300">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teams */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <Users className="w-5 h-5 text-slate-400" /><span className="text-white font-medium">Equipes</span>
          <Button variant="outline" size="sm" onClick={() => { setEditingTeam(null); setIsTeamDialogOpen(true); }} className="border-slate-600 text-slate-300 h-7 text-xs" data-testid="org-add-team"><Plus className="w-3 h-3 mr-1" /> Nova</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant={currentTeamId === null ? "default" : "outline"} size="sm" onClick={() => setCurrentTeamId(null)}
            className={currentTeamId === null ? "bg-teal-600 hover:bg-teal-700" : "border-slate-600 text-slate-300"}>
            Todos ({dubladores.length})
          </Button>
          {teams.map(t => (
            <div key={t.id} className="flex items-center gap-1">
              <Button variant={currentTeamId === t.id ? "default" : "outline"} size="sm" onClick={() => setCurrentTeamId(t.id)}
                className={currentTeamId === t.id ? "bg-teal-600 hover:bg-teal-700" : "border-slate-600 text-slate-300"}>
                {t.nome} ({dubladores.filter(d => d.team_id === t.id).length})
              </Button>
              {currentTeamId === t.id && (
                <div className="flex gap-0.5">
                  <button onClick={() => { setEditingTeam(t); setIsTeamDialogOpen(true); }} className="p-1 text-slate-400 hover:text-amber-400" data-testid={`org-edit-team-${t.id}`}><Edit3 className="w-3 h-3" /></button>
                  <button onClick={() => setDeleteTeamConfirm(t)} className="p-1 text-slate-400 hover:text-red-400" data-testid={`org-del-team-${t.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total", value: sorted.length, color: "text-white" },
          { label: "Média Geral", value: sorted.length > 0 ? (sorted.reduce((a, d) => a + calcNota(d), 0) / sorted.length).toFixed(2) : "0.00", color: "text-teal-400" },
          { label: "Top Score", value: sorted.length > 0 ? Math.max(...sorted.map(d => calcNota(d))).toFixed(2) : "0.00", color: "text-emerald-400" },
          { label: "Menor Score", value: sorted.length > 0 ? Math.min(...sorted.map(d => calcNota(d))).toFixed(2) : "0.00", color: "text-amber-400" }
        ].map((s, i) => (
          <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-400">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Action Buttons - Prominent */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <Button onClick={() => { setEditingDublador(null); setIsDialogOpen(true); }} className="bg-teal-600 hover:bg-teal-700 h-10" data-testid="org-add-dub"><Plus className="w-4 h-4 mr-2" /> Adicionar Dublador</Button>
        <Button onClick={() => setShowFeedbackDialog(true)} className="bg-orange-600 hover:bg-orange-700 h-10" data-testid="org-send-feedback"><FileText className="w-4 h-4 mr-2" /> Enviar Feedback</Button>
        <Button onClick={() => { fetchFeedbacks(); setShowFeedbackHistory(true); }} className="bg-indigo-600 hover:bg-indigo-700 h-10" data-testid="org-feedback-history"><ClipboardList className="w-4 h-4 mr-2" /> Histórico de Feedbacks</Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={sorted.length === 0} className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="org-export-csv"><FileText className="w-4 h-4 mr-1" /> CSV</Button>
        <Button variant="outline" size="sm" onClick={exportPDF} disabled={sorted.length === 0} className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="org-export-pdf"><Download className="w-4 h-4 mr-1" /> PDF</Button>
        <Button variant="outline" size="sm" onClick={exportListaExcel} disabled={dubladores.length === 0} className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/20" data-testid="org-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" /> Exportar</Button>
        <Button variant="outline" size="sm" onClick={() => importFileRef.current?.click()} className="border-amber-600 text-amber-400 hover:bg-amber-600/20" data-testid="org-import-list"><Upload className="w-4 h-4 mr-1" /> Importar</Button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col lg:flex-row justify-between gap-3">
        <div className="relative w-full lg:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input placeholder="Buscar por nome..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-slate-800/50 border-slate-700 text-white" data-testid="org-search" />
          {searchTerm && <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={filterCategoria} onValueChange={setFilterCategoria}>
            <SelectTrigger className="w-44 bg-slate-800/50 border-slate-700 text-slate-300"><SelectValue placeholder="Todas Categorias" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="masculino_grave">Masculino - Grave</SelectItem>
              <SelectItem value="masculino_aguda">Masculino - Aguda</SelectItem>
              <SelectItem value="feminino_grave">Feminino - Grave</SelectItem>
              <SelectItem value="feminino_aguda">Feminino - Aguda</SelectItem>
              {customCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setShowCatDialog(true)} className="border-purple-600 text-purple-400 hover:bg-purple-600/20" data-testid="org-manage-cats">
            <Settings className="w-4 h-4 mr-1" /> Categorias
          </Button>
        </div>
      </div>

      {/* Sort buttons */}
      <div className="flex flex-wrap gap-2">
        {sortOpts.map(o => (
          <Button key={o.value} variant={sortBy === o.value ? "default" : "outline"} size="sm" onClick={() => handleSort(o.value)}
            className={sortBy === o.value ? "bg-teal-600 hover:bg-teal-700" : "border-slate-600 text-slate-300"}>
            {o.label} {sortBy === o.value && (sortOrder === "asc" ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />)}
          </Button>
        ))}
      </div>

      {/* Cards */}
      {sorted.length === 0 ? (
        <div className="text-center py-16">
          <Mic2 className="w-14 h-14 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400">{searchTerm || filterCategoria ? "Nenhum resultado" : "Nenhum dublador cadastrado"}</p>
          {!searchTerm && !filterCategoria && <Button onClick={() => { setEditingDublador(null); setIsDialogOpen(true); }} className="bg-teal-600 hover:bg-teal-700 mt-4"><Plus className="w-4 h-4 mr-1" /> Adicionar</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map(d => {
            const ng = calcNota(d);
            return (
              <div key={d.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-colors" data-testid={`org-card-${d.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{d.nome}</h3>
                    {getCatLabel(d) && <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${getCatColor(d.categoria)}`}>{getCatLabel(d)}</span>}
                    <p className={`text-2xl font-bold font-mono mt-1 ${getNotaColor(ng)}`}>{ng.toFixed(2)}<span className="text-sm text-slate-500 ml-1">/5</span></p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingDublador(d); setIsDialogOpen(true); }} className="p-1.5 text-slate-500 hover:text-amber-400 hover:bg-slate-700 rounded" data-testid={`org-edit-${d.id}`}><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteConfirm(d)} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-700 rounded" data-testid={`org-del-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="space-y-2">
                  {["disponibilidade", "qualidade", "som", "comprometimento"].map(key => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-xs text-slate-400 capitalize">{key}</span>
                      <StarRating value={d[key]} readonly size="sm" />
                    </div>
                  ))}
                </div>
                {d.observacoes && <p className="mt-3 pt-3 border-t border-slate-700 text-xs text-slate-500 line-clamp-2">{d.observacoes}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==================== FORM DIALOGS ====================
const DubladorFormDialog = ({ open, onOpenChange, editingDublador, onSave, customCategories = [], siteUsers = [] }) => {
  const [form, setForm] = useState({ nome: "", user_id: "", disponibilidade: 0, qualidade: 0, som: 0, comprometimento: 0, observacoes: "", categoria: "", categorias: [] });
  useEffect(() => {
    if (editingDublador) setForm({ nome: editingDublador.nome || "", user_id: editingDublador.user_id || "", disponibilidade: editingDublador.disponibilidade || 0, qualidade: editingDublador.qualidade || 0, som: editingDublador.som || 0, comprometimento: editingDublador.comprometimento || 0, observacoes: editingDublador.observacoes || "", categoria: editingDublador.categoria || "", categorias: editingDublador.categorias || [] });
    else setForm({ nome: "", user_id: "", disponibilidade: 0, qualidade: 0, som: 0, comprometimento: 0, observacoes: "", categoria: "", categorias: [] });
  }, [editingDublador, open]);
  const ng = ((form.disponibilidade + form.qualidade + form.som + form.comprometimento) / 4).toFixed(2);
  const handleSubmit = () => { if (!form.nome.trim()) return toast.error("Nome obrigatório"); onSave(form); };
  
  const toggleCat = (catKey) => {
    const cats = form.categorias || [];
    setForm({ ...form, categorias: cats.includes(catKey) ? cats.filter(c => c !== catKey) : [...cats, catKey] });
  };
  
  const allCatOptions = [
    { key: "masculino_grave", label: "Masculino - Grave" },
    { key: "masculino_aguda", label: "Masculino - Aguda" },
    { key: "feminino_grave", label: "Feminino - Grave" },
    { key: "feminino_aguda", label: "Feminino - Aguda" },
    ...customCategories.map(c => ({ key: c.id, label: c.nome }))
  ];
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white">{editingDublador ? "Editar" : "Novo"} Dublador</DialogTitle>
          <DialogDescription className="text-slate-400">{editingDublador ? "Atualize as informações." : "Preencha os dados."}</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm text-slate-400 block mb-1">Vincular a usuário do site (opcional)</label>
            <select value={form.user_id} onChange={(e) => {
              const sel = siteUsers.find(u => u.id === e.target.value);
              setForm({ ...form, user_id: e.target.value, nome: sel ? (sel.artist_name || sel.name) : form.nome });
            }} className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm" data-testid="org-form-user-select">
              <option value="">-- Nenhum (nome manual) --</option>
              {siteUsers.map(u => (
                <option key={u.id} value={u.id}>{u.artist_name || u.name} ({u.role})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1">Nome</label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="bg-slate-900 border-slate-600 text-white" data-testid="org-form-nome" />
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1">Categorias de Voz (selecione múltiplas)</label>
            <div className="space-y-1 max-h-36 overflow-y-auto bg-slate-900 border border-slate-600 rounded-lg p-2">
              {allCatOptions.map(opt => (
                <label key={opt.key} className="flex items-center gap-2 p-1 hover:bg-slate-800 rounded cursor-pointer text-sm">
                  <input type="checkbox" className="accent-teal-500" checked={(form.categorias || []).includes(opt.key)} onChange={() => toggleCat(opt.key)} />
                  <span className="text-slate-300">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 space-y-3">
            <span className="text-sm text-white font-medium">Avaliações</span>
            {[{k:"disponibilidade",l:"Disponibilidade"},{k:"qualidade",l:"Qualidade"},{k:"som",l:"Som"},{k:"comprometimento",l:"Comprometimento"}].map(({k,l}) => (
              <div key={k} className="flex justify-between items-center">
                <span className="text-sm text-slate-400">{l}</span>
                <StarRating value={form[k]} onChange={(v) => setForm({...form, [k]: v})} />
              </div>
            ))}
            <div className="pt-3 border-t border-slate-700 flex justify-between items-center">
              <span className="text-sm font-medium text-teal-400">Nota Geral</span>
              <span className="text-lg font-bold text-teal-400 font-mono">{ng}/5</span>
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1">Observações</label>
            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} className="bg-slate-900 border-slate-600 text-white min-h-[80px]" data-testid="org-form-obs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
          <Button onClick={handleSubmit} className="bg-teal-600 hover:bg-teal-700" data-testid="org-form-save"><Check className="w-4 h-4 mr-1" /> Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TeamFormDialog = ({ open, onOpenChange, editingTeam, onSave }) => {
  const [nome, setNome] = useState("");
  useEffect(() => { setNome(editingTeam?.nome || ""); }, [editingTeam, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
        <DialogHeader><DialogTitle className="text-white">{editingTeam ? "Editar" : "Nova"} Equipe</DialogTitle></DialogHeader>
        <div className="py-3">
          <label className="text-sm text-slate-400 block mb-1">Nome da Equipe</label>
          <Input value={nome} onChange={(e) => setNome(e.target.value)} className="bg-slate-900 border-slate-600 text-white" data-testid="org-team-nome"
            onKeyDown={(e) => { if (e.key === 'Enter' && nome.trim()) onSave(nome); }} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
          <Button onClick={() => { if (!nome.trim()) return toast.error("Nome obrigatório"); onSave(nome); }} className="bg-teal-600 hover:bg-teal-700" data-testid="org-team-save"><Check className="w-4 h-4 mr-1" /> Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

